import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getActiveSubscriptionPlans,
  getPaymentMethods,
  addPaymentMethod,
  getInvoices,
  getInvoice,
  updateSubscriptionPaymentMethod,
} from 'src/services/billing';
import { downloadBillingHistoryAsCSV } from 'src/utils/csvUtils';

export const useBilling = () => {
  const queryClient = useQueryClient();

  const { data: subscriptionPlans, isLoading: isLoadingPlans } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: getActiveSubscriptionPlans,
  });

  const { data: paymentMethods, isLoading: isLoadingPaymentMethods } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: getPaymentMethods,
  });

  const { data: invoices, isLoading: isLoadingInvoices } = useQuery({
    queryKey: ['invoices'],
    queryFn: getInvoices,
  });

  const mutationAddPaymentMethod = useMutation({
    mutationFn: addPaymentMethod,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    },
  });

  const mutationUpdateSubscriptionPaymentMethod = useMutation({
    mutationFn: ({ subscriptionId, paymentMethodId }: { subscriptionId: string; paymentMethodId: string }) =>
      updateSubscriptionPaymentMethod(subscriptionId, paymentMethodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
    },
  });

  return {
    subscriptionPlans,
    paymentMethods,
    invoices,
    isLoading: isLoadingPlans || isLoadingPaymentMethods || isLoadingInvoices,
    addPaymentMethod: mutationAddPaymentMethod.mutate,
    updateSubscriptionPaymentMethod: mutationUpdateSubscriptionPaymentMethod.mutate,
    downloadBillingHistoryAsCSV,
  };
};

export const useInvoice = (invoiceId: string) => {
  return useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => getInvoice(invoiceId),
    enabled: !!invoiceId,
  });
};
